class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    // #CartDrawer-Overlay is the full-screen backdrop div. Its innerHTML gets
    // replaced whenever renderContents()/refreshCartDrawer() re-render
    // #CartDrawer (e.g. after every cart update), which would detach a
    // directly-bound listener. Listening at the persistent <cart-drawer>
    // root and checking the click target avoids that staleness issue.
    this.addEventListener("click", (evt) => {
      if (evt.target === this || evt.target.id === "CartDrawer-Overlay") {
        this.close();
      }
    });

    // Accessibility wiring for the header cart icon (#cart-icon-bubble).
    // Click-to-open is intentionally NOT wired up here yet - that lands in
    // step 1.3/1.4 alongside the header hover-open removal. This only makes
    // the cart icon behave like a proper button for a11y tooling.
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    // Guard: ensure subscribe and PUB_SUB_EVENTS are available (defined in
    // constants.js / pubsub.js, loaded before cart-drawer.js)
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === "cart-drawer-items") {
          return;
        }
        this.refreshCartDrawer();
      });
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
    // Drives the dark background overlay (body.is-overlay-active, see
    // frontend/styles/base/html.css) - the same mechanism cart-notification.js
    // and header-old.liquid's hover script use. Needed now that the header's
    // hover-open logic (which used to toggle this class) has been removed.
    if (typeof handleModalOverlay === "function") handleModalOverlay(true);
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
    if (typeof handleModalOverlay === "function") handleModalOverlay(false);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");

    // Step 1.4: the drawer is now the sole cart UI - the header's desktop
    // hover-open/close logic has been removed (modules/header/header.js).
    // This custom element self-wires the click/keyboard interaction so the
    // cart icon opens the drawer regardless of where it's rendered.
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code === "Enter" || event.code === "Space" || event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /**
   * Sync header cart count from Section Rendering API HTML.
   * Matches CartNotification / CartItems: replace #cart-icon-bubble innerHTML
   * with the cart-icon-bubble section body (not a missing #cart-icon-bubble-content).
   * Preserves the <a id="cart-icon-bubble"> node so click/a11y listeners stay intact.
   */
  updateCartIconBubble(sectionHtml) {
    if (!sectionHtml) return;
    const cartIconBubble = document.getElementById("cart-icon-bubble");
    if (!cartIconBubble) return;
    cartIconBubble.innerHTML = this.getSectionInnerHTML(
      sectionHtml,
      ".shopify-section",
    );
  }

  refreshCartDrawer() {
    const cartInner = document.querySelector(".drawer__inner");
    if (cartInner) {
      fetch(
        `${window.routes?.cart_url || "/cart"}?sections=cart-drawer,cart-icon-bubble`,
      )
        .then((response) => response.json())
        .then((responseJSON) => {
          this.updateCartIconBubble(responseJSON["cart-icon-bubble"]);

          const cartDrawerSectionHtml = responseJSON["cart-drawer"];
          if (cartDrawerSectionHtml) {
            const parsedSection = new DOMParser().parseFromString(
              cartDrawerSectionHtml,
              "text/html",
            );
            const newCartInner = parsedSection.querySelector(".drawer__inner");
            if (newCartInner) {
              cartInner.innerHTML = newCartInner.innerHTML;
            }

            const cartDrawer = document.querySelector("cart-drawer");
            if (cartDrawer) {
              cartDrawer.classList.toggle(
                "is-empty",
                !parsedSection.querySelector("cart-drawer-items"),
              );
            }
          }
        })
        .catch((e) => {
          console.error("Error refreshing cart drawer:", e);
          const cartDrawerItems = this.querySelector("cart-drawer-items");
          if (
            cartDrawerItems &&
            typeof cartDrawerItems.onCartUpdate === "function"
          ) {
            cartDrawerItems.onCartUpdate();
          }
        });
    } else {
      const cartDrawerItems = this.querySelector("cart-drawer-items");
      if (
        cartDrawerItems &&
        typeof cartDrawerItems.onCartUpdate === "function"
      ) {
        cartDrawerItems.onCartUpdate();
      }
    }
  }

  renderContents(parsedState, open = true) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;

      // Header bubble: patch via dedicated helper (same as refreshCartDrawer).
      // Do not use querySelector(selector) here — cart-icon-bubble has no
      // selector and must update #cart-icon-bubble's innerHTML, not replace the node.
      if (section.id === "cart-icon-bubble") {
        this.updateCartIconBubble(sectionHtml);
        return;
      }

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        sectionHtml,
        section.selector,
      );
    });

    if (!open) return;

    // Mobile vs desktop ATC branching, ported from pni-bulgaria's
    // modules/cart-drawer/cart-drawer.js renderContents(). On mobile we don't
    // want to open the full drawer for every add-to-cart - instead show a
    // small auto-dismissing toast (quick-cart-notification) with the added
    // product, while still refreshing the drawer's contents silently in the
    // background so its state is correct whenever the user does open it.
    if (window.innerWidth > 991) {
      this.open();
    } else {
      const data = {
        id: parsedState.id,
        title: parsedState.title,
        vendor: parsedState.vendor,
        price: parsedState.price,
        image: parsedState.featured_image?.url || parsedState.image,
        url: parsedState.url,
      };

      document
        .querySelector("quick-cart-notification")
        ?.setAttribute("data-product", JSON.stringify(data));
      this.refreshCartDrawer();
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const element = parsed.querySelector(selector);
    if (!element) {
      console.warn(`Selector "${selector}" not found in HTML for cart-drawer`);
      // Fallback: return the shopify-section innerHTML if it exists
      const fallback = parsed.querySelector(".shopify-section");
      return fallback ? fallback.innerHTML : html;
    }
    return element.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      // Requested on /cart/add so ATC can update the header count without a
      // full refresh. Same section id CartNotification / CartDrawerItems use.
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// Dedicated, self-contained +/- quantity stepper used ONLY by the cart
// drawer's item list (snippets/cart-drawer.liquid). Deliberately NOT part of
// frontend/components/quantity-input.js (the sitewide dropdown-style picker
// used on product pages etc.) so this can be sized/styled freely without any
// risk to other pages. Reads/writes its own visible number input and calls
// the same cartHandler.updateQuantity() wiring the rest of the cart uses.
class CartDrawerQuantity extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="number"]');
    this.cartHandler = this.closest("cart-drawer-items");

    // Collapsed by default (matches bulgaria's reference behavior): only the
    // number is visible until the box itself is clicked, which reveals the
    // -/+ buttons. Once open it stays open for that row.
    this.addEventListener("click", (event) => {
      const isOpen = this.classList.contains("is-open");

      if (event.target.closest(".js-qty-minus")) {
        if (isOpen) this.step(-1);
      } else if (event.target.closest(".js-qty-plus")) {
        if (isOpen) this.step(1);
      } else if (!isOpen) {
        this.classList.add("is-open");
        this.input?.focus();
      }
    });

    // Collapse again when focus leaves the whole control.
    this.addEventListener("focusout", (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.classList.remove("is-open");
      }
    });
  }

  step(direction) {
    if (!this.input) return;

    const stepValue = parseInt(this.input.step, 10) || 1;
    const minAttr = this.input.getAttribute("min");
    const maxAttr = this.input.getAttribute("max");
    const min = minAttr !== null && minAttr !== "" ? parseInt(minAttr, 10) : 0;
    const max =
      maxAttr !== null && maxAttr !== "" ? parseInt(maxAttr, 10) : Infinity;

    let value = parseInt(this.input.value, 10);
    if (Number.isNaN(value)) value = min;

    value += direction * stepValue;
    if (value < min) value = min;
    if (value > max) value = max;

    this.input.value = value;

    this.cartHandler &&
      this.cartHandler.updateQuantity(
        this.dataset.index,
        value,
        this.input.name,
        this.dataset.variantId,
      );
  }
}

if (!customElements.get("cart-drawer-quantity")) {
  customElements.define("cart-drawer-quantity", CartDrawerQuantity);
}

// "Order again" tab: reorder from a past order into the cart.
// Ported from pni-bulgaria modules/cart-drawer/cart-drawer.js, adapted to:
// - animax's own quantity stepper markup (plain +/- buttons, no shared
//   quantity-input/quantity-popover web component - this is a self-contained
//   local quantity field, only read at submit time)
// - animax's cart-drawer's refreshCartDrawer() (defined above on CartDrawer)
// - currency formatting via Intl.NumberFormat using the cart's currency
//   (data-currency attribute), since animax doesn't load @shopify/theme-currency
class AddPreviousOrderToCart extends HTMLElement {
  constructor() {
    super();
    this.orderTotalPrice = 0;
    this.orderTotalPriceElement = this.querySelector(".js-order-total-price");
    this.currency = this.dataset.currency || "RON";
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    if (!this.form) return;

    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.handleSubmit();
    });

    // Quantity stepper buttons (+/-), scoped to each order item row
    this.addEventListener("click", (event) => {
      const minusButton = event.target.closest(".js-order-qty-minus");
      const plusButton = event.target.closest(".js-order-qty-plus");
      if (!minusButton && !plusButton) return;

      const row = (minusButton || plusButton).closest(".js-order-item");
      const input = row?.querySelector(".js-order-item-quantity-input");
      if (!input) return;

      const min = parseInt(input.min, 10) || 1;
      const max = parseInt(input.max, 10);
      let value = parseInt(input.value, 10) || min;

      if (minusButton) value = Math.max(min, value - 1);
      if (plusButton) value = Number.isNaN(max) ? value + 1 : Math.min(max, value + 1);

      input.value = value;
      this.calculateOrderTotalPrice();
    });

    // Recalculate the running total whenever a quantity input changes directly
    this.addEventListener("change", (event) => {
      if (event.target.classList.contains("js-order-item-quantity-input")) {
        this.calculateOrderTotalPrice();
      }
    });

    // Recalculate when the active swiper slide changes (different order shown)
    this.addEventListener("click", () => {
      this.calculateOrderTotalPrice();
    });
  }

  handleSubmit() {
    const items = [];

    this.products = this.querySelectorAll(".swiper-slide-active .js-order-item");

    this.products.forEach((product) => {
      const id = +product.querySelector('input[name="order-item-id"]').value;
      const quantityInput = product.querySelector(".js-order-item-quantity-input");
      const quantity = +quantityInput.value;

      if (quantity && quantity > 0 && !quantityInput.disabled) {
        items.push({ id, quantity });
      }
    });

    if (!items.length) return;

    this.addPreviousOrderToCart(items);
  }

  addPreviousOrderToCart(items) {
    this.setAttribute("data-status", "adding");

    fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "xmlhttprequest",
      },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json())
      .then(() => {
        this.setAttribute("data-status", "added");

        const cartDrawer = document.querySelector("cart-drawer");
        cartDrawer?.refreshCartDrawer();
        cartDrawer?.classList.remove("is-empty");

        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-drawer-items" });
        }

        setTimeout(() => {
          this.removeAttribute("data-status");
        }, 1000);
      })
      .catch((err) => console.error("Error adding previous order to cart:", err));
  }

  calculateOrderTotalPrice() {
    this.selectedOrderProducts = this.querySelectorAll(".swiper-slide-active .js-order-item");
    this.orderTotalPrice = 0;

    this.selectedOrderProducts.forEach((product) => {
      const price = +product.querySelector(".js-order-item-price-input").value;
      const quantityInput = product.querySelector(".js-order-item-quantity-input");
      const quantity = +quantityInput.value;

      if (quantityInput.disabled) return;

      this.orderTotalPrice += price * quantity;
    });

    if (!this.orderTotalPriceElement) return;

    try {
      this.orderTotalPriceElement.textContent = new Intl.NumberFormat(
        document.documentElement.lang || undefined,
        { style: "currency", currency: this.currency },
      ).format(this.orderTotalPrice / 100);
    } catch (e) {
      // Unknown/unsupported currency code - fall back to a plain number
      this.orderTotalPriceElement.textContent = (this.orderTotalPrice / 100).toFixed(2);
    }
  }
}

if (!customElements.get("add-previous-order-to-cart")) {
  customElements.define("add-previous-order-to-cart", AddPreviousOrderToCart);
}

// Mobile add-to-cart toast, shown instead of opening the full cart drawer.
// Ported from pni-bulgaria's modules/cart-drawer/cart-drawer.js
// QuickCartNotification, adapted to:
// - animax's own money-formatting convention: Intl.NumberFormat against a
//   data-currency attribute (the same convention AddPreviousOrderToCart above
//   already uses), since animax doesn't load @shopify/theme-currency or
//   expose window.themeBuiltt.moneyFormat.
// - inline <svg> markup for the icons (same approach bulgaria uses), with
//   colors adapted to animax's palette (--color-blue accent).
class QuickCartNotification extends HTMLElement {
  static observedAttributes = ["data-product"];

  constructor() {
    super();
    this.currency = this.dataset.currency || "RON";

    this.addEventListener("click", (e) => {
      if (e.target.closest(".js-quick-close")) {
        e.preventDefault();
        this.removeAttribute("data-open");
      }
    });
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "data-product" && newValue) {
      this.renderNotification(JSON.parse(newValue));
      this.setAttribute("data-open", "true");

      clearTimeout(this.dismissTimeout);
      this.dismissTimeout = setTimeout(() => {
        this.removeAttribute("data-open");
      }, 2000);
    }
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: "currency",
        currency: this.currency,
      }).format((cents || 0) / 100);
    } catch (e) {
      return ((cents || 0) / 100).toFixed(2);
    }
  }

  renderNotification(product) {
    const title = product.title || "";
    const vendor = product.vendor || "";
    const imageSrc = product.image || "";
    const imageAlt = product.title || "";

    this.innerHTML = `<div class="flex gap-2.5 relative px-4 py-2.5">
      <div class="absolute top-1 left-1 z-1">
        <svg class="confirmation-icon size-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30" width="30" height="30" fill="none">
          <rect width="30" height="30" fill="#43A345" rx="15"></rect>
          <path stroke="#fff" stroke-linecap="round" stroke-width="1.5" d="m10 14.857 3.571 3.572L20 12"></path>
        </svg>
      </div>
      <a href="${product.url}" aria-label="${title}" class="block size-18 shrink-0">
        <div class="media-wrapper w-full h-full">
          <img class="object-scale-down w-full h-full" src="${imageSrc}" width="72" height="72" alt="${imageAlt}" loading="eager" decoding="async">
        </div>
      </a>
      <div class="w-[calc(100%-4.5rem)]">
        <a href="${product.url}" aria-label="${title}" class="text-sm line-clamp-2 pr-4 block">
          ${vendor ? `<strong class="text-sm font-bold uppercase block">${vendor}</strong>` : ""}
          ${title}
        </a>
        <span class="text-base font-bold block mt-2">${this.formatMoney(product.price)}</span>
      </div>
      <button aria-label="Close" class="js-quick-close absolute flex items-center justify-center size-6 focus:outline-none top-3 right-3 cursor-pointer">
        <svg class="pointer-events-none size-4" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M17 1L1 17" stroke="#2C2A2A" stroke-width="1.26338" stroke-miterlimit="10"/>
          <path d="M1 1L17 17" stroke="#2C2A2A" stroke-width="1.26338" stroke-miterlimit="10"/>
        </svg>
      </button>
    </div>`;
  }
}

if (!customElements.get("quick-cart-notification")) {
  customElements.define("quick-cart-notification", QuickCartNotification);
}

if (!customElements.get("bogo-gift-handler")) {
  customElements.define(
    "bogo-gift-handler",
    class BogoGiftHandler extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;
        this.evaluate();
      }

      get giftVariantId() {
        return String(this.dataset.giftVariantId || "").trim();
      }

      get triggerQuantity() {
        return parseInt(this.dataset.triggerQuantity || "0", 10) || 0;
      }

      get giftQuantity() {
        return parseInt(this.dataset.giftQuantity || "0", 10) || 0;
      }

      get giftKeys() {
        const raw = String(this.dataset.giftKeys || "").trim();
        if (!raw) return [];
        return raw
          .split(",")
          .map((key) => key.trim())
          .filter(Boolean);
      }

      get isEnabled() {
        return String(this.dataset.enabled) !== "false";
      }

      get giftAvailable() {
        return String(this.dataset.giftAvailable) !== "false";
      }

      async evaluate() {
        if (window.__bogoGiftHandlerLock) {
          return;
        }

        if (!this.isEnabled) {
          if (this.giftQuantity > 0 && this.giftKeys.length) {
            await this.setGiftQuantity(0);
          }
          return;
        }

        if (!this.giftVariantId) {
          return;
        }

        const targetQuantity = this.giftAvailable ? this.triggerQuantity : 0;

        if (targetQuantity !== this.giftQuantity) {
          await this.setGiftQuantity(targetQuantity);
        }
      }

      async setGiftQuantity(targetQuantity) {
        window.__bogoGiftHandlerLock = true;
        try {
          const keys = this.giftKeys;

          if (keys.length === 0) {
            if (targetQuantity > 0) {
              await fetch("/cart/add.js", {
                method: "POST",
                credentials: "same-origin",
                headers: {
                  "Content-Type": "application/json",
                  "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify({
                  items: [
                    {
                      id: this.giftVariantId,
                      quantity: targetQuantity,
                      properties: {
                        _bogo_gift: "true",
                      },
                    },
                  ],
                }),
              });
            }
          } else {
            const updates = {};
            keys.forEach((key, index) => {
              updates[key] = index === 0 ? targetQuantity : 0;
            });

            await fetch("/cart/update.js", {
              method: "POST",
              credentials: "same-origin",
              headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
              },
              body: JSON.stringify({ updates }),
            });
          }

          await this.refreshUI();
        } catch (error) {
          console.error("Failed to sync bogo gift quantity:", error);
        } finally {
          window.__bogoGiftHandlerLock = false;
        }
      }

      async refreshUI() {
        const isCartPage = this.dataset.renderContext === "cart-page";

        if (isCartPage) {
          const cartItems = document.querySelector("cart-items");
          if (cartItems && typeof cartItems.getSectionsToRender === "function") {
            const sectionsToRender = cartItems.getSectionsToRender();
            const freshSections = await getFreshSections(sectionsToRender);
            if (freshSections) {
              sectionsToRender.forEach((section) => {
                const elementToReplace =
                  document.getElementById(section.id)?.querySelector(section.selector) ||
                  document.getElementById(section.id);
                const sectionHtml = freshSections[section.section];
                if (elementToReplace && sectionHtml) {
                  elementToReplace.innerHTML = cartItems.getSectionInnerHTML(sectionHtml, section.selector);
                }
              });
            }
          }
        }

        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer && typeof cartDrawer.refreshCartDrawer === "function") {
          cartDrawer.refreshCartDrawer();
        }
      }
    }
  );
}
